import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from './authSlice';

interface OfflineAction {
  id: string;
  type: string;
  payload: any;
  timestamp: number;
  retryCount: number;
  endpoint?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | string;
  priority?: number;
  estimatedDuration?: number;
  size?: number;
}

interface CacheMetadata {
  lastSync: number | null;
  ttl: number; // Time to live in milliseconds
}

interface SyncProgress {
  isRunning: boolean;
  currentOperation: string | null;
  totalOperations: number;
  completedOperations: number;
  errors: string[];
  percentage: number;
  estimatedTimeRemaining?: number;
  currentItem?: number;
  startTime?: number;
  syncId?: string;
}

interface SyncHistoryRecord {
  id: string;
  timestamp: number;
  duration: number;
  itemsSynced: number;
  errors: string[];
  success: boolean;
  percentage: number;
}

interface SyncStatistics {
  totalSyncs: number;
  successfulSyncs: number;
  successRate: number;
  averageDuration: number;
  totalItemsSynced: number;
  recentErrors: string[];
}

interface CachedDataInfo {
  templates: CacheMetadata;
  categories: CacheMetadata;
  items: CacheMetadata;
  rooms: CacheMetadata;
  reviews: CacheMetadata;
}

interface OfflineState {
  isOnline: boolean;
  pendingActions: OfflineAction[];
  syncInProgress: boolean;
  lastSyncTime: number | null;
  cacheMetadata: CachedDataInfo;
  syncProgress: SyncProgress;
  syncHistory: SyncHistoryRecord[];
  syncStatistics: SyncStatistics;
  cachedData: {
    templates: any[];
    categories: any[];
    items: any[];
    rooms: any[];
    reviews: any[];
  };
}

type OfflineRootState = {
  offline: OfflineState;
};

export interface OfflineSyncSummary {
  isOnline: boolean;
  syncInProgress: boolean;
  pendingActionsCount: number;
  lastSyncTime: number | null;
  syncProgress: SyncProgress;
  syncStatistics: SyncStatistics;
}

const initialState: OfflineState = {
  isOnline: true,
  pendingActions: [],
  syncInProgress: false,
  lastSyncTime: null,
  cacheMetadata: {
    templates: { lastSync: null, ttl: 24 * 60 * 60 * 1000 }, // 24 hours
    categories: { lastSync: null, ttl: 24 * 60 * 60 * 1000 }, // 24 hours
    items: { lastSync: null, ttl: 24 * 60 * 60 * 1000 }, // 24 hours
    rooms: { lastSync: null, ttl: 6 * 60 * 60 * 1000 }, // 6 hours
    reviews: { lastSync: null, ttl: 1 * 60 * 60 * 1000 }, // 1 hour
  },
  syncProgress: {
    isRunning: false,
    currentOperation: null,
    totalOperations: 0,
    completedOperations: 0,
    errors: [],
    percentage: 0,
    estimatedTimeRemaining: 0,
    currentItem: 0,
    startTime: undefined,
    syncId: undefined,
  },
  syncHistory: [],
  syncStatistics: {
    totalSyncs: 0,
    successfulSyncs: 0,
    successRate: 0,
    averageDuration: 0,
    totalItemsSynced: 0,
    recentErrors: [],
  },
  cachedData: {
    templates: [],
    categories: [],
    items: [],
    rooms: [],
    reviews: [],
  },
};

const offlineSlice = createSlice({
  name: 'offline',
  initialState,
  reducers: {
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
    addPendingAction: (state, action: PayloadAction<Omit<OfflineAction, 'id' | 'timestamp' | 'retryCount'>>) => {
      state.pendingActions.push({
        ...action.payload,
        id: `${Date.now()}-${Math.random()}`,
        timestamp: Date.now(),
        retryCount: 0,
      });
    },
    removePendingAction: (state, action: PayloadAction<string>) => {
      state.pendingActions = state.pendingActions.filter(a => a.id !== action.payload);
    },
    incrementRetryCount: (state, action: PayloadAction<string>) => {
      const actionItem = state.pendingActions.find(a => a.id === action.payload);
      if (actionItem) {
        actionItem.retryCount += 1;
      }
    },
    setSyncInProgress: (state, action: PayloadAction<boolean>) => {
      state.syncInProgress = action.payload;
    },
    setLastSyncTime: (state, action: PayloadAction<number>) => {
      state.lastSyncTime = action.payload;
    },
    clearPendingActions: (state) => {
      state.pendingActions = [];
    },
    // Cache management actions
    updateCacheMetadata: (state, action: PayloadAction<{
      dataType: keyof CachedDataInfo;
      metadata: Partial<CacheMetadata>
    }>) => {
      const { dataType, metadata } = action.payload;
      state.cacheMetadata[dataType] = {
        ...state.cacheMetadata[dataType],
        ...metadata,
      };
    },
    setCachedData: (state, action: PayloadAction<{
      dataType: keyof typeof state.cachedData;
      data: any[]
    }>) => {
      const { dataType, data } = action.payload;
      state.cachedData[dataType] = data;
      // Update cache metadata
      state.cacheMetadata[dataType].lastSync = Date.now();
    },
    clearCachedData: (state, action: PayloadAction<keyof typeof state.cachedData>) => {
      const dataType = action.payload;
      state.cachedData[dataType] = [];
      state.cacheMetadata[dataType].lastSync = null;
    },
    clearAllCachedData: (state) => {
      state.cachedData = {
        templates: [],
        categories: [],
        items: [],
        rooms: [],
        reviews: [],
      };
      Object.keys(state.cacheMetadata).forEach(key => {
        const dataType = key as keyof CachedDataInfo;
        state.cacheMetadata[dataType].lastSync = null;
      });
    },
    // Sync progress actions
    startSyncProgress: (state, action: PayloadAction<{ totalOperations: number; syncId?: string }>) => {
      state.syncProgress = {
        isRunning: true,
        currentOperation: null,
        totalOperations: action.payload.totalOperations,
        completedOperations: 0,
        errors: [],
        percentage: 0,
        estimatedTimeRemaining: 0,
        currentItem: 0,
        startTime: Date.now(),
        syncId: action.payload.syncId,
      };
    },
    updateSyncProgress: (state, action: PayloadAction<{
      currentOperation?: string;
      completedOperations?: number;
      errors?: string[];
      percentage?: number;
      estimatedTimeRemaining?: number;
      currentItem?: number;
    }>) => {
      const {
        currentOperation,
        completedOperations,
        errors,
        percentage,
        estimatedTimeRemaining,
        currentItem
      } = action.payload;

      if (currentOperation !== undefined) {
        state.syncProgress.currentOperation = currentOperation;
      }
      if (completedOperations !== undefined) {
        state.syncProgress.completedOperations = completedOperations;
        // Auto-calculate percentage if not provided
        if (percentage === undefined && state.syncProgress.totalOperations > 0) {
          state.syncProgress.percentage = Math.round(
            (completedOperations / state.syncProgress.totalOperations) * 100
          );
        }
      }
      if (percentage !== undefined) {
        state.syncProgress.percentage = percentage;
      }
      if (estimatedTimeRemaining !== undefined) {
        state.syncProgress.estimatedTimeRemaining = estimatedTimeRemaining;
      }
      if (currentItem !== undefined) {
        state.syncProgress.currentItem = currentItem;
      }
      if (errors) {
        state.syncProgress.errors.push(...errors);
      }
    },
    completeSyncProgress: (state) => {
      state.syncProgress.isRunning = false;
      state.syncProgress.currentOperation = 'Sync completed';
      state.syncProgress.completedOperations = state.syncProgress.totalOperations;
      state.syncProgress.percentage = 100;
      state.syncProgress.estimatedTimeRemaining = 0;
    },
    addSyncError: (state, action: PayloadAction<string>) => {
      state.syncProgress.errors.push(action.payload);
    },
    clearSyncProgress: (state) => {
      state.syncProgress = {
        isRunning: false,
        currentOperation: null,
        totalOperations: 0,
        completedOperations: 0,
        errors: [],
        percentage: 0,
        estimatedTimeRemaining: 0,
        currentItem: 0,
        startTime: undefined,
        syncId: undefined,
      };
    },
    // Sync history actions
    addSyncHistoryRecord: (state, action: PayloadAction<SyncHistoryRecord>) => {
      state.syncHistory.unshift(action.payload);

      // Keep only last 50 records
      if (state.syncHistory.length > 50) {
        state.syncHistory = state.syncHistory.slice(0, 50);
      }

      // Update statistics
      const totalSyncs = state.syncHistory.length;
      const successfulSyncs = state.syncHistory.filter(record => record.success).length;
      const averageDuration = totalSyncs > 0
        ? state.syncHistory.reduce((sum, record) => sum + record.duration, 0) / totalSyncs
        : 0;
      const totalItemsSynced = state.syncHistory.reduce((sum, record) => sum + record.itemsSynced, 0);

      state.syncStatistics = {
        totalSyncs,
        successfulSyncs,
        successRate: totalSyncs > 0 ? (successfulSyncs / totalSyncs) * 100 : 0,
        averageDuration: Math.round(averageDuration),
        totalItemsSynced,
        recentErrors: state.syncHistory.slice(0, 5).flatMap(record => record.errors),
      };
    },
    clearSyncHistory: (state) => {
      state.syncHistory = [];
      state.syncStatistics = {
        totalSyncs: 0,
        successfulSyncs: 0,
        successRate: 0,
        averageDuration: 0,
        totalItemsSynced: 0,
        recentErrors: [],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Clear offline state on logout
      .addCase(logout.fulfilled, (state) => {
        state.pendingActions = [];
        state.syncInProgress = false;
        state.lastSyncTime = null;
      });
  },
});

export const {
  setOnlineStatus,
  addPendingAction,
  removePendingAction,
  incrementRetryCount,
  setSyncInProgress,
  setLastSyncTime,
  clearPendingActions,
  updateCacheMetadata,
  setCachedData,
  clearCachedData,
  clearAllCachedData,
  startSyncProgress,
  updateSyncProgress,
  completeSyncProgress,
  addSyncError,
  clearSyncProgress,
  addSyncHistoryRecord,
  clearSyncHistory,
} = offlineSlice.actions;

export const selectOfflineState = (state: OfflineRootState): OfflineState => state.offline;

export const selectOfflineSyncSummary = (state: OfflineRootState): OfflineSyncSummary => {
  const offline = state.offline;
  const pendingActions = offline.pendingActions ?? [];
  const syncProgress = offline.syncProgress ?? initialState.syncProgress;
  const syncStatistics = offline.syncStatistics ?? initialState.syncStatistics;

  return {
    isOnline: offline.isOnline ?? initialState.isOnline,
    syncInProgress: Boolean(offline.syncInProgress || syncProgress.isRunning),
    pendingActionsCount: pendingActions.length,
    lastSyncTime: offline.lastSyncTime ?? null,
    syncProgress,
    syncStatistics,
  };
};

export default offlineSlice.reducer;
